<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import TimePeriodV2 from '@/common/components/molecules/timePeriod/TimePeriodV2.vue';
import type { TimePeriodData } from '@/types/common.types';
import { useInternational } from '@/common/locale';
import { getLocaleKey } from '@/common/utils/commonUtils';
import type { TimePeriodInfo } from '../types';
import { getFormalPeriod, getRangeDisplayTime, getLiveDisplayTime } from '../utils';
import { CUSTOM_PERIOD_PREFIX, DEFAULT_PERIOD_ITEMS } from '../constants';

interface Props {
  modelValue: TimePeriodInfo;
  items?: Readonly<TimePeriodData[]>;
  displayDateOnly?: boolean;
  disabled?: boolean;
}

interface Emits {
  (e: 'update:modelValue', value: TimePeriodInfo): void;
  (e: 'select', period: string): void;
  (e: 'change-period'): void;
  (e: 'dropdown-open'): void;
}

const props = withDefaults(defineProps<Props>(), {
  items: () => DEFAULT_PERIOD_ITEMS,
  displayDateOnly: false,
  disabled: false,
});

const emit = defineEmits<Emits>();

// ============================================================
// State
// ============================================================

const { t } = useInternational();

const periodItems = ref<TimePeriodData[]>(
  props.items.map<TimePeriodData>(({ name, value, shortName }) => ({
    name: t(`WORD.TIME_PERIOD_ITEMS.${getLocaleKey(name)}`),
    value,
    shortName,
  })),
);

const selectedPeriod = ref<string>(props.modelValue.timePeriod || 'p10m');

// ============================================================
// Computed
// ============================================================

const isPeriodItem = computed(() => {
  const period = getFormalPeriod(selectedPeriod.value);
  const periodValues = periodItems.value.map(({ value }) => value);
  return periodValues.includes(period);
});

// ============================================================
// Custom Period Management
// ============================================================

const updateCustomPeriodItem = ({
  period,
  displayTime,
}: {
  period: string;
  displayTime: string;
}) => {
  const periodItem = {
    name: displayTime,
    value: `${CUSTOM_PERIOD_PREFIX}${period}`,
    shortName: period.split(' ')[0].slice(1),
  };

  const customPeriodIdx = periodItems.value.findIndex(({ value }) =>
    value.includes(CUSTOM_PERIOD_PREFIX),
  );
  if (customPeriodIdx > -1) {
    periodItems.value[customPeriodIdx] = periodItem;
  } else {
    periodItems.value.unshift(periodItem);
  }

  selectedPeriod.value = periodItems.value[0].value;
};

// ============================================================
// Handlers
// ============================================================

const handleChangePeriod = () => {
  // 동일 기간 선택한 경우에는 무시 (custom 선택)
  if (selectedPeriod.value.includes(CUSTOM_PERIOD_PREFIX)) {
    return;
  }

  const period = getFormalPeriod(selectedPeriod.value);

  emit('select', period);
  emit('change-period');
};

// ============================================================
// Watchers
// ============================================================

// modelValue 변경 시 period item 업데이트
watch(
  () => props.modelValue,
  (info) => {
    const { timePeriod, fromTime, toTime, isPaused } = info;

    const displayTime =
      isPaused || timePeriod === 'pa'
        ? getRangeDisplayTime({
            fromTime,
            toTime,
            dateOnly: props.displayDateOnly,
          })
        : getLiveDisplayTime({ toTime });

    updateCustomPeriodItem({
      period: getFormalPeriod(timePeriod),
      displayTime,
    });
  },
  { deep: true, immediate: true },
);

// items prop 변경 시 periodItems 업데이트 (custom period item 보존)
watch(
  () => props.items,
  (newItems, oldItems) => {
    if (import.meta.env.DEV && oldItems) {
      const isSameContent =
        newItems.length === oldItems.length &&
        newItems.every((item, i) => item.value === oldItems[i].value);
      if (isSameContent) {
        console.warn(
          '[PeriodSelector] items prop의 참조가 변경되었지만 내용은 동일합니다. ' +
            '불필요한 re-render를 방지하려면 인라인 배열 대신 상수를 사용하세요. ' +
            '(예: DEFAULT_PERIOD_ITEMS_ALL)',
        );
      }
    }

    const existingCustomItem = periodItems.value.find(({ value }) =>
      value.includes(CUSTOM_PERIOD_PREFIX),
    );

    periodItems.value = newItems.map<TimePeriodData>(({ name, value, shortName }) => ({
      name: t(`WORD.TIME_PERIOD_ITEMS.${getLocaleKey(name)}`),
      value,
      shortName,
    }));

    if (existingCustomItem) {
      periodItems.value.unshift(existingCustomItem);
    }
  },
  { deep: true },
);

// ============================================================
// Expose
// ============================================================

defineExpose({
  selectedPeriod,
  periodItems,
  isPeriodItem,
  updateCustomPeriodItem,
});
</script>

<template>
  <time-period-v2
    v-model="selectedPeriod"
    :periods="periodItems"
    :disabled="props.disabled"
    :use-calendar="false"
    @change-period="handleChangePeriod"
    @dropdown-open="emit('dropdown-open')"
  />
</template>
